import { useEffect, useRef, useState } from 'react'
import { cmsTextOrFallback } from '@/api/helpers'
import { cmsTextTitle } from '@/styles/cmsText'

import './About.css'
import type { AboutCollageBySlot, AboutCollageSlot } from '@/api/aboutCollage'
import type { AboutFooterStat } from '@/api/types'
import AuditCtaButton from '@/components/ui/AuditCtaButton'
import { colors } from '@/styles/colors'

interface AboutProps {
  isMobile: boolean
  cmsReady?: boolean
  title?: string
  description?: string
  collageBySlot?: AboutCollageBySlot
  footerStats?: AboutFooterStat[]
}

const DEFAULT_ABOUT_TITLE = 'Your Partner for Growth in the MENA Region'
const DEFAULT_ABOUT_DESCRIPTION =
  'Digitrek empowers businesses across the GCC and MENA to achieve sustainable growth. We move you beyond operational chaos with clear guidance and expertly crafted digital projects.\nOur Dubai-based team of expert strategists, engineers, and data scientists doesn\'t just build technology; we build partnerships.\nThrough transparency and a deep understanding of the regional market, we deliver results you can measure.'

const ABOUT_CTA = 'Contact Us'

const DEFAULT_FOOTER_STATS: AboutFooterStat[] = [
  { title: '3+', description: 'Years of Experience' },
  { title: '100%', description: 'GCC-Based Team' },
  { title: 'Odoo & AWS', description: 'Certified Partners' },
]

/** Split combined CMS title into title + description to match Figma (e.g. "100% GCC-Based Team") */
function normalizeDesktopFooterStat(stat: AboutFooterStat, index: number): AboutFooterStat {
  const fallback = DEFAULT_FOOTER_STATS[index]
  const rawTitle = stat.title.trim() || fallback.title
  const rawDescription = stat.description.trim()

  if (rawDescription) {
    return {
      title: rawTitle || fallback.title,
      description: rawDescription,
    }
  }

  const head = fallback.title
  const tail = fallback.description

  if (rawTitle.startsWith(head)) {
    const afterHead = rawTitle.slice(head.length).trim()
    if (afterHead.startsWith(tail)) {
      return { title: head, description: afterHead }
    }
    if (afterHead) {
      return { title: head, description: afterHead }
    }
    return { title: head, description: tail }
  }

  const splitPatterns: RegExp[] = [
    /^(.+?)\s+(Years of Experience)/i,
    /^(100%)\s+(GCC-Based Team)/i,
    /^(Odoo\s*&\s*[\w]+)\s+(.+)/i,
  ]
  const match = rawTitle.match(splitPatterns[index])
  if (match) {
    return { title: match[1].trim(), description: match[2].trim() }
  }

  return { title: rawTitle, description: tail }
}

function resolveFooterStats(footerStats?: AboutFooterStat[], cmsReady = true): AboutFooterStat[] {
  if (!cmsReady && !footerStats?.length) {
    return DEFAULT_FOOTER_STATS.map(() => ({ title: '', description: '' }))
  }

  return DEFAULT_FOOTER_STATS.map((fallback, index) => {
    const fromCms = footerStats?.[index]
    const merged: AboutFooterStat = {
      title: fromCms?.title?.trim() || fallback.title,
      description: fromCms?.description?.trim() || fallback.description,
    }
    return normalizeDesktopFooterStat(merged, index)
  })
}

function DesktopFooterStatCell({
  stat,
  index,
}: {
  stat: AboutFooterStat
  index: number
}) {
  const { title, description } = normalizeDesktopFooterStat(stat, index)

  return (
    <div className={`about-card__stat about-card__stat--${index}`} aria-label={`${title} ${description}`}>
      <span className="about-card__stat-title" title={cmsTextTitle(title)}>
        {title}
      </span>
      <span className="about-card__stat-desc" title={cmsTextTitle(description)}>
        {description}
      </span>
    </div>
  )
}

/** First line ends on "Growth" — matches Figma (e.g. "Your Partner for Growth" / "in the MENA Region"). */
function splitAboutTitleIntoLines(title: string): string[] {
  const trimmed = title.trim()
  if (!trimmed) return ['']

  const growthBreak = trimmed.match(/^(.+?\bGrowth)\s+([\s\S]+)$/i)
  if (growthBreak) {
    return [growthBreak[1].trim(), growthBreak[2].trim()]
  }

  return [trimmed]
}

function DesktopAboutTitle({
  title,
  className,
}: {
  title: string
  className?: string
}) {
  const lines = splitAboutTitleIntoLines(title)

  return (
    <h2 className={className} title={cmsTextTitle(title)}>
      {lines.map((line, index) => (
        <span key={index} style={{ display: 'block' }}>
          {line}
        </span>
      ))}
    </h2>
  )
}

function splitDescriptionFirstWord(text: string): { firstWord: string; rest: string } {
  const trimmed = text.trimStart()
  const match = trimmed.match(/^(\S+)([\s\S]*)$/)
  if (!match) return { firstWord: '', rest: text }
  return { firstWord: match[1], rest: match[2] }
}

function DesktopAboutDescription({ text }: { text: string }) {
  const { firstWord, rest } = splitDescriptionFirstWord(text)
  if (!firstWord) return <>{text}</>
  return (
    <>
      <span className="about-card__description-lead">{firstWord}</span>
      <span className="about-card__description-body">{rest}</span>
    </>
  )
}

/** Paragraph breaks as a single newline — avoids blank spacer lines with white-space: pre-line */
function normalizeAboutParagraphs(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return trimmed

  const collapsed = trimmed.replace(/\n{2,}/g, '\n')
  if (/\n/.test(collapsed)) return collapsed

  return collapsed
    .replace(/([.!?])\s+(Our Dubai-based team)/i, '$1\n$2')
    .replace(/([.!?])\s+(Through transparency)/i, '$1\n$2')
}

function collageSlotSrc(
  slot: AboutCollageSlot,
  fallback: string,
  collageBySlot?: AboutCollageBySlot
): string {
  return collageBySlot?.[slot] ?? fallback
}

const teamImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center center',
  display: 'block',
}

function aboutCollageFrameStyle(top: number, left: number): React.CSSProperties {
  return {
    position: 'absolute',
    top: `${top}px`,
    left: `${left}px`,
    width: '220px',
    height: '300px',
    borderRadius: '16px',
    overflow: 'hidden',
    zIndex: 1,
    boxSizing: 'border-box',
  }
}

type DesktopCollageImage = {
  key: AboutCollageSlot
  src: string
  alt: string
  top: number
  left: number
  objectPosition?: string
}

const DESKTOP_ABOUT_IMAGES: DesktopCollageImage[] = [
  {
    key: '59' as const,
    src: '/images/2149060246.jpg',
    alt: 'Team collaboration',
    top: 19,
    left: 1540,
    objectPosition: '78% center',
  },
  {
    key: '57' as const,
    src: '/images/bf404d08198a86ab83b0df723bc84087.jpg',
    alt: 'Team meeting',
    top: 171,
    left: 1288,
    objectPosition: '70% center',
  },
  { key: '60' as const, src: '/images/3735.jpg', alt: 'Team member', top: 519, left: 1288 },
  {
    key: '56' as const,
    src: '/images/2149006892.jpg',
    alt: 'Team workspace',
    top: 367,
    left: 1540,
    objectPosition: '72% center',
  },
  { key: '53' as const, src: '/images/2148040518.jpg', alt: 'Team collaboration', top: 373, left: 1036 },
]

function MobileFooterStatCell({ stat, index }: { stat: AboutFooterStat; index: number }) {
  const { title, description } = normalizeDesktopFooterStat(stat, index)

  return (
    <div className="about-mobile__stat" aria-label={`${title} ${description}`}>
      <div className="about-mobile__stat-title" title={cmsTextTitle(title)}>
        {title}
      </div>
      <div className="about-mobile__stat-desc" title={cmsTextTitle(description)}>
        {description}
      </div>
    </div>
  )
}

function MobileAboutCollage({ collageBySlot }: { collageBySlot?: AboutCollageBySlot }) {
  return (
    <div className="about-mobile__collage">
      <div className="about-mobile__collage-row--top">
        <div className="about-mobile__collage-frame about-mobile__collage-frame--59">
          <img
            src={collageSlotSrc('59', '/images/2149060246.jpg', collageBySlot)}
            alt="Team collaboration"
            style={{ ...teamImageStyle, objectPosition: '78% center' }}
          />
        </div>
        <div className="about-mobile__collage-frame about-mobile__collage-frame--53">
          <img
            src={collageSlotSrc('53', '/images/2148040518.jpg', collageBySlot)}
            alt="Team member"
            style={teamImageStyle}
          />
        </div>
      </div>

      <div className="about-mobile__collage-row--bottom">
        <div className="about-mobile__collage-frame about-mobile__collage-frame--57">
          <img
            src={collageSlotSrc('57', '/images/bf404d08198a86ab83b0df723bc84087.jpg', collageBySlot)}
            alt="Team meeting"
            style={{ ...teamImageStyle, objectPosition: '70% center' }}
          />
        </div>
        <div className="about-mobile__collage-frame about-mobile__collage-frame--60">
          <img
            src={collageSlotSrc('60', '/images/3735.jpg', collageBySlot)}
            alt="Team workspace"
            style={teamImageStyle}
          />
        </div>
        <div className="about-mobile__collage-frame about-mobile__collage-frame--56">
          <img
            src={collageSlotSrc('56', '/images/2149006892.jpg', collageBySlot)}
            alt="Team collaboration"
            style={{ ...teamImageStyle, objectPosition: '72% center' }}
          />
        </div>
      </div>
    </div>
  )
}

export default function About({
  isMobile,
  cmsReady = true,
  title,
  description,
  collageBySlot,
  footerStats,
}: AboutProps) {
  const stats = resolveFooterStats(footerStats, cmsReady)
  const aboutTitle = cmsTextOrFallback(title, DEFAULT_ABOUT_TITLE, cmsReady)
  const aboutDescription = cmsTextOrFallback(description, DEFAULT_ABOUT_DESCRIPTION, cmsReady)
  const normalizedAboutDescription = normalizeAboutParagraphs(aboutDescription)
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  if (isMobile) {
    const fade = () =>
      `transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`

    return (
      <section id="about" ref={sectionRef} className="about-section--mobile" aria-label="About us">
        <div className="about-mobile__inner">
          <h2 title={cmsTextTitle(aboutTitle)} className={`about-mobile__title ${fade()}`}>
            {aboutTitle}
          </h2>

          <p
            title={cmsTextTitle(normalizedAboutDescription)}
            className={`about-mobile__description ${fade()}`}
            style={{ transitionDelay: '100ms' }}
          >
            {normalizedAboutDescription}
          </p>

          <div className={fade()} style={{ transitionDelay: '200ms' }}>
            <MobileAboutCollage collageBySlot={collageBySlot} />
          </div>

          <div className={`about-mobile__stats ${fade()}`} style={{ transitionDelay: '300ms' }}>
            {stats.map((stat, index) => (
              <MobileFooterStatCell key={index} stat={stat} index={index} />
            ))}
          </div>

          <div className={`about-mobile__cta ${fade()}`} style={{ transitionDelay: '400ms' }}>
            <AuditCtaButton href="#contact" variant="compact">
              {ABOUT_CTA}
            </AuditCtaButton>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      id="about"
      ref={sectionRef}
      aria-label="About us"
      style={{
        position: 'relative',
        width: '100%',
        background: colors.background.white,
        padding: '80px 0',
      }}
    >
      <div className="about-card--desktop">
        <DesktopAboutTitle
          title={aboutTitle}
          className={`about-card__title transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        />

        <p
          title={cmsTextTitle(normalizedAboutDescription)}
          className={`about-card__description transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{ transitionDelay: '100ms' }}
        >
          <DesktopAboutDescription text={normalizedAboutDescription} />
        </p>

        <div
          className={`about-card__stats transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{ transitionDelay: '200ms' }}
        >
          {stats.map((stat, index) => (
            <DesktopFooterStatCell key={index} stat={stat} index={index} />
          ))}
          </div>

        <AuditCtaButton
          href="#contact"
          variant="about"
          title={ABOUT_CTA}
          className={`about-card__cta${isVisible ? ' about-card__cta--visible' : ''}`}
          style={{ transitionDelay: isVisible ? '300ms' : '0ms' }}
        >
          {ABOUT_CTA}
        </AuditCtaButton>

        {/* Image collage */}
        {DESKTOP_ABOUT_IMAGES.map((imageConfig, index) => (
          <div
            key={imageConfig.key}
            className={`transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{
              ...aboutCollageFrameStyle(imageConfig.top, imageConfig.left),
              transitionDelay: `${300 + index * 100}ms`,
            }}
          >
            <img
              src={collageSlotSrc(imageConfig.key, imageConfig.src, collageBySlot)}
              alt={imageConfig.alt}
              style={{
                ...teamImageStyle,
                objectPosition: imageConfig.objectPosition ?? teamImageStyle.objectPosition,
              }}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

